import React, { FunctionComponent, useState } from 'react'
import {
  Button,
  Stack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Input,
  InputLeftElement,
  InputGroup,
  Select,
  Text
} from '@chakra-ui/react'
import { startCase } from 'lodash'
import {
  CommunityFeatures,
  InternalFeatures,
  ExternalFeatures,
  PortfolioHomeFeatures
} from '@homevest/types/homes'
import { PORTFOLIO_HOME_SECURITY_OPTIONS } from '@homevest/utils/homes'

import { BaseFormField } from 'components/ChakraToolkit/FormField'
import {
  useUpsertCommunityFeatures,
  useUpsertInternalFeatures,
  useUpsertExternalFeatures
} from './UpsertFeatures'

interface EditFeaturesProps {
  portfolioHomeId: string
  communityId: string | null
  portfolioHomeFeatures: PortfolioHomeFeatures
}

const EditFeatures: FunctionComponent<React.PropsWithChildren<EditFeaturesProps>> = ({
  portfolioHomeId,
  communityId,
  portfolioHomeFeatures
}) => {
  const [communityFeatures, setCommunityFeatures] = useState<CommunityFeatures>(
    portfolioHomeFeatures.community
  )
  const [internalFeatures, setInternalFeatures] = useState<InternalFeatures>(
    portfolioHomeFeatures.internal
  )
  const [externalFeatures, setExternalFeatures] = useState<ExternalFeatures>(
    portfolioHomeFeatures.external
  )

  const [communityErrors] = useState<Record<keyof CommunityFeatures, string>>(
    {} as Record<keyof CommunityFeatures, string>
  )
  const [internalErrors] = useState<Record<keyof InternalFeatures, string>>(
    {} as Record<keyof InternalFeatures, string>
  )
  const [externalErrors, setExternalErrors] = useState<Record<keyof ExternalFeatures, string>>(
    {} as Record<keyof ExternalFeatures, string>
  )

  const editCommunityFeatures = useUpsertCommunityFeatures()
  const editInternalFeatures = useUpsertInternalFeatures()
  const editExternalFeatures = useUpsertExternalFeatures()

  const areExternalFeaturesValid = () => {
    if (!externalFeatures.security) {
      setExternalErrors({
        ...externalErrors,
        security: 'Please select a security option'
      })
      return false
    }

    setExternalErrors({ ...externalErrors, security: '' })
    return true
  }

  const handleSubmit = (featureType: 'community' | 'internal' | 'external') => {
    if (
      featureType === 'community' &&
      communityId &&
      Object.entries(communityFeatures).some(([key, value]) => key !== 'name' && value !== null)
    ) {
      editCommunityFeatures({ communityId, data: communityFeatures })
    }

    if (
      featureType === 'internal' &&
      Object.entries(internalFeatures).some(([_, value]) => value !== null)
    ) {
      editInternalFeatures({ portfolioHomeId, data: internalFeatures })
    }

    if (
      featureType === 'external' &&
      Object.entries(externalFeatures).some(([_, value]) => value !== null)
    ) {
      if (areExternalFeaturesValid()) {
        editExternalFeatures({ portfolioHomeId, data: externalFeatures })
      }
    }
  }

  return (
    <Stack>
      <Tabs isFitted variant='solid-rounded' colorScheme='teal' size='sm'>
        <TabList>
          {communityId && <Tab>Community</Tab>}
          <Tab>Internal</Tab>
          <Tab>External</Tab>
        </TabList>
        <TabPanels mt={4}>
          {communityId && (
            <TabPanel>
              <Stack spacing={4}>
                <Text>
                  <Text as='span' fontWeight='bold'>
                    Name
                  </Text>
                  : {communityFeatures.name}
                </Text>
                <BaseFormField<CommunityFeatures>
                  field='hoaContactName'
                  label='HOA Contact Name'
                  isRequired={false}
                  errors={communityErrors}
                >
                  <Input
                    value={communityFeatures.hoaContactName ?? ''}
                    onChange={(e) =>
                      setCommunityFeatures({
                        ...communityFeatures,
                        hoaContactName: e.target.value || null
                      })
                    }
                  />
                </BaseFormField>
                <BaseFormField<CommunityFeatures>
                  field='hoaContactEmail'
                  label='HOA Contact Email'
                  isRequired={false}
                  errors={communityErrors}
                >
                  <Input
                    value={communityFeatures.hoaContactEmail ?? ''}
                    onChange={(e) =>
                      setCommunityFeatures({
                        ...communityFeatures,
                        hoaContactEmail: e.target.value || null
                      })
                    }
                  />
                </BaseFormField>
                <BaseFormField<CommunityFeatures>
                  field='amenityFee'
                  label='Amenity Fee'
                  isRequired={false}
                  errors={communityErrors}
                >
                  <InputGroup>
                    <InputLeftElement>$</InputLeftElement>
                    <Input
                      value={communityFeatures.amenityFee ?? ''}
                      onChange={(e) =>
                        setCommunityFeatures({
                          ...communityFeatures,
                          amenityFee: Number(e.target.value) || null
                        })
                      }
                    />
                  </InputGroup>
                </BaseFormField>
                <BaseFormField<CommunityFeatures>
                  field='amenityDetails'
                  label='Amenity Details'
                  isRequired={false}
                  errors={communityErrors}
                >
                  <Input
                    value={communityFeatures.amenityDetails ?? ''}
                    onChange={(e) =>
                      setCommunityFeatures({
                        ...communityFeatures,
                        amenityDetails: e.target.value || null
                      })
                    }
                  />
                </BaseFormField>
                <BaseFormField<CommunityFeatures>
                  field='pool'
                  label='Pool'
                  isRequired={false}
                  errors={communityErrors}
                >
                  <Select
                    value={communityFeatures.pool === null ? '' : String(communityFeatures.pool)}
                    onChange={(e) =>
                      setCommunityFeatures({
                        ...communityFeatures,
                        pool:
                          e.target.value === 'true'
                            ? true
                            : e.target.value === 'false'
                            ? false
                            : null
                      })
                    }
                  >
                    <option value=''>Select an option</option>
                    <option value='true'>Yes</option>
                    <option value='false'>No</option>
                  </Select>
                </BaseFormField>
                <BaseFormField<CommunityFeatures>
                  field='playground'
                  label='Playground'
                  isRequired={false}
                  errors={communityErrors}
                >
                  <Select
                    value={
                      communityFeatures.playground === null
                        ? ''
                        : String(communityFeatures.playground)
                    }
                    onChange={(e) =>
                      setCommunityFeatures({
                        ...communityFeatures,
                        playground:
                          e.target.value === 'true'
                            ? true
                            : e.target.value === 'false'
                            ? false
                            : null
                      })
                    }
                  >
                    <option value=''>Select an option</option>
                    <option value='true'>Yes</option>
                    <option value='false'>No</option>
                  </Select>
                </BaseFormField>
                <BaseFormField<CommunityFeatures>
                  field='landscaping'
                  label='Landscaping'
                  isRequired={false}
                  errors={communityErrors}
                >
                  <Select
                    value={
                      communityFeatures.landscaping === null
                        ? ''
                        : String(communityFeatures.landscaping)
                    }
                    onChange={(e) =>
                      setCommunityFeatures({
                        ...communityFeatures,
                        landscaping:
                          e.target.value === 'true'
                            ? true
                            : e.target.value === 'false'
                            ? false
                            : null
                      })
                    }
                  >
                    <option value=''>Select an option</option>
                    <option value='true'>Yes</option>
                    <option value='false'>No</option>
                  </Select>
                </BaseFormField>
                <BaseFormField<CommunityFeatures>
                  field='security'
                  label='Security'
                  isRequired={false}
                  errors={communityErrors}
                >
                  <Select
                    value={
                      communityFeatures.security === null ? '' : String(communityFeatures.security)
                    }
                    onChange={(e) =>
                      setCommunityFeatures({
                        ...communityFeatures,
                        security:
                          e.target.value === 'true'
                            ? true
                            : e.target.value === 'false'
                            ? false
                            : null
                      })
                    }
                  >
                    <option value=''>Select an option</option>
                    <option value='true'>Yes</option>
                    <option value='false'>No</option>
                  </Select>
                </BaseFormField>
                <BaseFormField<CommunityFeatures>
                  field='dogPark'
                  label='Dog Park'
                  isRequired={false}
                  errors={communityErrors}
                >
                  <Select
                    value={
                      communityFeatures.dogPark === null ? '' : String(communityFeatures.dogPark)
                    }
                    onChange={(e) =>
                      setCommunityFeatures({
                        ...communityFeatures,
                        dogPark:
                          e.target.value === 'true'
                            ? true
                            : e.target.value === 'false'
                            ? false
                            : null
                      })
                    }
                  >
                    <option value=''>Select an option</option>
                    <option value='true'>Yes</option>
                    <option value='false'>No</option>
                  </Select>
                </BaseFormField>
                <BaseFormField<CommunityFeatures>
                  field='nature'
                  label='Nature'
                  isRequired={false}
                  errors={communityErrors}
                >
                  <Input
                    value={communityFeatures.nature ?? ''}
                    onChange={(e) =>
                      setCommunityFeatures({
                        ...communityFeatures,
                        nature: e.target.value || null
                      })
                    }
                  />
                </BaseFormField>
                <Button colorScheme='teal' size='sm' onClick={() => handleSubmit('community')}>
                  Submit Community Features
                </Button>
              </Stack>
            </TabPanel>
          )}
          <TabPanel>
            <Stack spacing={4}>
              <BaseFormField<InternalFeatures>
                field='washerDryer'
                label='Washer/Dryer'
                isRequired={false}
                errors={internalErrors}
              >
                <Select
                  value={
                    internalFeatures.washerDryer === null
                      ? ''
                      : String(internalFeatures.washerDryer)
                  }
                  onChange={(e) =>
                    setInternalFeatures({
                      ...internalFeatures,
                      washerDryer:
                        e.target.value === 'true' ? true : e.target.value === 'false' ? false : null
                    })
                  }
                >
                  <option value=''>Select an option</option>
                  <option value='true'>Yes</option>
                  <option value='false'>No</option>
                </Select>
              </BaseFormField>
              <BaseFormField<InternalFeatures>
                field='basement'
                label='Basement'
                isRequired={false}
                errors={internalErrors}
              >
                <Select
                  value={
                    internalFeatures.basement === null ? '' : String(internalFeatures.basement)
                  }
                  onChange={(e) =>
                    setInternalFeatures({
                      ...internalFeatures,
                      basement:
                        e.target.value === 'true' ? true : e.target.value === 'false' ? false : null
                    })
                  }
                >
                  <option value=''>Select an option</option>
                  <option value='true'>Yes</option>
                  <option value='false'>No</option>
                </Select>
              </BaseFormField>
              <BaseFormField<InternalFeatures>
                field='warranties'
                label='Warranties'
                isRequired={false}
                errors={internalErrors}
              >
                <Input
                  value={internalFeatures.warranties ?? ''}
                  onChange={(e) =>
                    setInternalFeatures({
                      ...internalFeatures,
                      warranties: e.target.value || null
                    })
                  }
                />
              </BaseFormField>
              <BaseFormField<InternalFeatures>
                field='appliances'
                label='Appliances'
                isRequired={false}
                errors={internalErrors}
              >
                <Input
                  value={internalFeatures.appliances ?? ''}
                  onChange={(e) =>
                    setInternalFeatures({
                      ...internalFeatures,
                      appliances: e.target.value || null
                    })
                  }
                />
              </BaseFormField>
              <BaseFormField<InternalFeatures>
                field='utilitiesException'
                label='Utilities Exception'
                isRequired={false}
                errors={internalErrors}
              >
                <Input
                  value={internalFeatures.utilitiesException ?? ''}
                  onChange={(e) =>
                    setInternalFeatures({
                      ...internalFeatures,
                      utilitiesException: e.target.value || null
                    })
                  }
                />
              </BaseFormField>
              <Button colorScheme='teal' size='sm' onClick={() => handleSubmit('internal')}>
                Submit Internal Features
              </Button>
            </Stack>
          </TabPanel>
          <TabPanel>
            <Stack spacing={4}>
              <BaseFormField<ExternalFeatures>
                field='security'
                label='Security'
                isRequired
                errors={externalErrors}
              >
                <Select
                  value={externalFeatures.security ?? ''}
                  onChange={(e) =>
                    setExternalFeatures({
                      ...externalFeatures,
                      security: e.target.value
                    })
                  }
                >
                  <option value=''>Select a security option</option>
                  {Object.values(PORTFOLIO_HOME_SECURITY_OPTIONS).map((security) => (
                    <option key={security} value={security}>
                      {startCase(security)}
                    </option>
                  ))}
                </Select>
              </BaseFormField>
              <BaseFormField<ExternalFeatures>
                field='mailboxInfo'
                label='Mailbox Info'
                isRequired={false}
                errors={externalErrors}
              >
                <Input
                  value={externalFeatures.mailboxInfo ?? ''}
                  onChange={(e) =>
                    setExternalFeatures({
                      ...externalFeatures,
                      mailboxInfo: e.target.value || null
                    })
                  }
                />
              </BaseFormField>
              <BaseFormField<ExternalFeatures>
                field='garage'
                label='Garage'
                isRequired={false}
                errors={externalErrors}
              >
                <Input
                  value={externalFeatures.garage ?? ''}
                  onChange={(e) =>
                    setExternalFeatures({
                      ...externalFeatures,
                      garage: e.target.value || null
                    })
                  }
                />
              </BaseFormField>
              <BaseFormField<ExternalFeatures>
                field='parking'
                label='Parking'
                isRequired={false}
                errors={externalErrors}
              >
                <Input
                  value={externalFeatures.parking ?? ''}
                  onChange={(e) =>
                    setExternalFeatures({
                      ...externalFeatures,
                      parking: e.target.value || null
                    })
                  }
                />
              </BaseFormField>
              <BaseFormField<ExternalFeatures>
                field='gate'
                label='Gate'
                isRequired={false}
                errors={externalErrors}
              >
                <Input
                  value={externalFeatures.gate ?? ''}
                  onChange={(e) =>
                    setExternalFeatures({
                      ...externalFeatures,
                      gate: e.target.value || null
                    })
                  }
                />
              </BaseFormField>
              <BaseFormField<ExternalFeatures>
                field='fence'
                label='Fence'
                isRequired={false}
                errors={externalErrors}
              >
                <Input
                  value={externalFeatures.fence ?? ''}
                  onChange={(e) =>
                    setExternalFeatures({
                      ...externalFeatures,
                      fence: e.target.value || null
                    })
                  }
                />
              </BaseFormField>
              <BaseFormField<ExternalFeatures>
                field='shed'
                label='Shed'
                isRequired={false}
                errors={externalErrors}
              >
                <Input
                  value={externalFeatures.shed ?? ''}
                  onChange={(e) =>
                    setExternalFeatures({
                      ...externalFeatures,
                      shed: e.target.value || null
                    })
                  }
                />
              </BaseFormField>
              <Button colorScheme='teal' size='sm' onClick={() => handleSubmit('external')}>
                Submit External Features
              </Button>
            </Stack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Stack>
  )
}

export default EditFeatures
